import {
  Building,
  BuildingStId,
  Equipment,
  EquipmentCategory,
  EquipmentGroup,
  EquipmentGroups,
  EquipmentGroupStId,
  Offer
} from 'app/types/types'
import * as R from 'ramda'
import { getEquipmentByGroup } from '../nemo/offer'
import { Icons } from './client-constants'
import { t } from 'i18next'
import { getDataFromSessionStorage, getEquipmentText, multiValuedKeys } from './utils'
import { BuildingEquipment, StatusBadgeProps } from 'app/types/ui/opportunity'
import {
  DeleteGroupOption,
  EquipmentByCategory,
  EquipmentFieldConfig,
  EquipmentGroupAccordionProps,
  GroupEquipmentDetails,
  GroupOption
} from 'app/types/ui/grouping'
import { hasAnyScopeMissing } from './selection-util'
import { EquipmentByCategoryForPricingSummary } from 'app/types/ui/pricing'
import { getColumnsConfigByCategory } from './equipment-columns-config'

// Equipment details mapper configuration (translation keys to property keys)
const equipmentFieldsConfig: EquipmentFieldConfig = {
  elevator: [
    { translationKey: 'xlAssessmentWizard.equipment.APFComponent', propertyKey: 'apfComponent' },
    {
      translationKey: 'xlAssessmentWizard.equipment.koneConnectivityDeviceType',
      propertyKey: 'koneConnectivityDeviceType'
    },
    { translationKey: 'xlAssessmentWizard.elevator.doorsCount', propertyKey: 'doorsCount' },
    {
      translationKey: 'xlAssessmentWizard.equipment.manufacturingSerialnumber',
      propertyKey: 'manufacturingSerialnumber'
    },
    { translationKey: 'xlAssessmentWizard.equipment.koneEquipmentNumber', propertyKey: 'koneEquipmentNumber' },
    { translationKey: 'xlAssessmentWizard.equipment.year', propertyKey: 'year' },
    { translationKey: 'xlAssessmentWizard.elevator.ratedSpeed', propertyKey: 'ratedSpeed' },
    { translationKey: 'xlAssessmentWizard.elevator.ratedLoad', propertyKey: 'ratedLoad' },
    { translationKey: 'xlAssessmentWizard.elevator.technicalPlatform', propertyKey: 'technicalPlatform' },
    { translationKey: 'xlAssessmentWizard.elevator.additionalDoors', propertyKey: 'additionalDoors' },
    { translationKey: 'xlAssessmentWizard.equipment.operationalEnvironment', propertyKey: 'operationalEnvironment' },
    { translationKey: 'xlAssessmentWizard.equipment.manufacturer', propertyKey: 'manufacturer' }
  ],
  escalator: [
    { translationKey: 'xlAssessmentWizard.escalator.rise', propertyKey: 'rise' },
    { translationKey: 'xlAssessmentWizard.escalator.length', propertyKey: 'length' },
    {
      translationKey: 'xlAssessmentWizard.equipment.manufacturingSerialnumber',
      propertyKey: 'manufacturingSerialnumber'
    },
    { translationKey: 'xlAssessmentWizard.equipment.koneEquipmentNumber', propertyKey: 'koneEquipmentNumber' },
    { translationKey: 'xlAssessmentWizard.equipment.year', propertyKey: 'year' },
    { translationKey: 'xlAssessmentWizard.equipment.equipmentType', propertyKey: 'equipmentType' },
    { translationKey: 'xlAssessmentWizard.escalator.escalatorType', propertyKey: 'escalatorType' },
    { translationKey: 'xlAssessmentWizard.equipment.manufacturer', propertyKey: 'manufacturer' },
    { translationKey: 'xlAssessmentWizard.equipment.operationalEnvironment', propertyKey: 'operationalEnvironment' },
    { translationKey: 'xlAssessmentWizard.escalator.numberOfDrives', propertyKey: 'numberOfDrives' },
    { translationKey: 'xlAssessmentWizard.escalator.operationMode', propertyKey: 'operationMode' },
    {
      translationKey: 'xlAssessmentWizard.escalator.operationalEnvironmentCondition',
      propertyKey: 'operationalEnvironmentCondition'
    }
  ]
}

// Helper function to get equipment details with proper typing
export function getEquipmentDetailsTyped<T extends EquipmentCategory>(
  equipment: EquipmentByCategory[T] | null
): Array<{ label: string; value: string }> {
  if (!equipment || !equipment.equipmentCategory) {
    return []
  }

  const { equipmentCategory } = equipment

  const equipmentFieldsForGroupDetailsModal = equipmentFieldsConfig[equipmentCategory as EquipmentCategory]

  const { columns } = getColumnsConfigByCategory(
    equipment as EquipmentByCategoryForPricingSummary[T],
    (value: string) => value
  )

  if (!columns) {
    return []
  }

  return columns
    .filter(column => equipmentFieldsForGroupDetailsModal?.find(field => field.propertyKey === column.propertyKey))
    .map(column => ({
      label: column.header,
      value:
        typeof column.accessor === 'function'
          ? String(column.accessor(equipment as EquipmentByCategoryForPricingSummary[T]) || '-')
          : String(equipment[column.accessor as keyof typeof equipment] ?? '-')
    }))
}

export const groupHandleCheckboxChange = <T>(prev: Set<T>, key: T, checked: boolean, size?: number) => {
  const newSet = new Set(prev)
  checked ? newSet.add(key) : size && newSet.size > size ? newSet.delete(key) : !size && newSet.delete(key)
  return newSet
}

export const sortEquipmentCategoriesComparator = (categoryA: string, categoryB: string): number =>
  categoryA === 'elevator' && categoryB !== 'elevator'
    ? -1
    : categoryA !== 'elevator' && categoryB === 'elevator'
      ? 1
      : 0

export const countByEquipmentCategory = R.countBy((e: Equipment) => e.equipmentCategory)

export const getGroupOptions = (equipmentGroup: EquipmentGroups) => {
  if (!equipmentGroup) {
    return []
  }
  const groupOptionsArray = Object.values(equipmentGroup).map((group: EquipmentGroup) => ({
    key: group.salesToolId as string,
    value: group.name
  }))
  const sortedGroupOptions = R.sortBy(groupDetail => groupDetail?.value.toLowerCase(), groupOptionsArray)
  return sortedGroupOptions
}

export const getSelectedGroupForEquipment = (buildings: Building[]) => {
  if (!buildings) {
    return {}
  }
  return buildings.reduce((acc: Record<NonNullable<EquipmentGroupStId>, string>, building: Building) => {
    if (building.details?.equipment?.length > 0) {
      const equipmentWithSelectedGroup = building.details.equipment.reduce(
        (equipmentAcc: Record<NonNullable<EquipmentGroupStId>, string>, equipment: Equipment) => ({
          ...equipmentAcc,
          [equipment.salesToolId]: equipment.groupId
        }),
        {}
      )
      return { ...acc, ...equipmentWithSelectedGroup }
    }
    return acc
  }, {})
}

export const getGroupDetailsForLocation = (buildings: Building[]) => {
  if (!buildings) {
    return {}
  }
  return buildings.reduce((acc: Record<BuildingStId, EquipmentGroupStId[]>, building: Building) => {
    if (building.details?.equipment?.length > 0) {
      const groupDetailForLocation = building.details.equipment.reduce<EquipmentGroupStId[]>(
        (equipmentGroupIds, equipment) => {
          const equipmentGroupID = equipment.groupId
          return equipmentGroupIds.includes(equipmentGroupID)
            ? equipmentGroupIds
            : [...equipmentGroupIds, equipmentGroupID]
        },
        []
      )
      acc[building.salesToolId] = groupDetailForLocation
    }
    return acc
  }, {})
}

export const getFormattedBuildingDescription = (building: Building): string => {
  const { details } = building

  const { buildingDescription, buildingAddress, buildingCity } = details
  if (buildingDescription) {
    return buildingDescription
  }

  return buildingAddress || buildingCity
    ? `${buildingAddress || t('details.noAddress')}, ${buildingCity || t('assessment.cityMissing')}`
    : t('assessment.newBuilding')
}

export const getBuildingDescriptionForGroup = (buildings: Building[], buildingId: string): string => {
  const currentBuilding = buildings.find(building => building.salesToolId === buildingId)
  return currentBuilding ? getFormattedBuildingDescription(currentBuilding) : ''
}

export const getEquipmentDataByGroup = (buildings: Building[], equipmentGroups: EquipmentGroups) => {
  if (!equipmentGroups) {
    return [] as EquipmentGroupAccordionProps[]
  }

  const groupSalesToolIdArray: string[] = Object.keys(equipmentGroups)
  const equipmentArrayBasedOnGroupId = getEquipmentByGroup(buildings)

  const processedDataByGroup: EquipmentGroupAccordionProps[] = groupSalesToolIdArray.map((groupId: string) => {
    const sortedEquipmentArray = equipmentArrayBasedOnGroupId[groupId]?.sort((equipmentA, equipmentB) =>
      sortEquipmentCategoriesComparator(equipmentA.equipmentCategory, equipmentB.equipmentCategory)
    )
    const equipmentDetails =
      sortedEquipmentArray?.map(equipment => {
        // Equipment tags for future use
        const equipmentTags: StatusBadgeProps[] = []

        return {
          equipment,
          equipmentTags,
          equipmentIcon: Icons[equipment.equipmentCategory as keyof typeof Icons],
          locationDescription: getBuildingDescriptionForGroup(buildings, equipment.buildingId)
        }
      }) || []

    const { salesToolId: id, name } = equipmentGroups[groupId]

    const currentDataObj = {
      id: id as string,
      name,
      equipmentDetails
    }

    return currentDataObj
  })
  const equipmentDataSortByGroup = R.sortBy(groupDetail => groupDetail.name.toLowerCase(), processedDataByGroup)

  return equipmentDataSortByGroup
}

export const getEquipmentDataByLocation = (buildings: Building[]) => {
  if (!buildings) {
    return [] as EquipmentGroupAccordionProps[]
  }

  const processedDataByLocation: EquipmentGroupAccordionProps[] = buildings.map(building => {
    const name = getFormattedBuildingDescription(building)
    const {
      details: { salesToolId: id, equipment: equipments }
    } = building

    const equipmentDetails = equipments.map(equipment => {
      // Equipment tags for future use
      const equipmentTags: StatusBadgeProps[] = []

      return {
        equipment,
        equipmentTags,
        equipmentIcon: Icons[equipment.equipmentCategory as keyof typeof Icons]
      }
    })

    const currentDataObj = {
      id,
      name,
      equipmentDetails
    }

    return currentDataObj
  })

  const equipmentDataSortByLocation = R.sortBy(
    locationDetail => locationDetail.name.toLowerCase(),
    processedDataByLocation
  )

  return equipmentDataSortByLocation
}

export const cleanString = (value: string) => value.replace(/\s+/g, ' ').trim()

export function isEquipmentGroupNameValid(name: string, groups: EquipmentGroup[], id?: string): boolean {
  return (
    name.length > 0 &&
    name.length <= 50 &&
    R.pipe(
      R.filter((group: EquipmentGroup) => id !== group.salesToolId),
      R.none((group: EquipmentGroup) => name.toLowerCase() === group.name.toLowerCase())
    )(groups)
  )
}

export const getDetailTranslation = (key: string, value: string): string => {
  const getTranslatedText = (translatedText: string, v: string) =>
    translatedText && translatedText.indexOf('choices') > 0 ? v : translatedText
  if (multiValuedKeys.includes(key) && value.search(',') > 0) {
    const isMultiValue = value.split(',')

    return (
      (isMultiValue &&
        isMultiValue.length > 0 &&
        isMultiValue.map(v => getTranslatedText(t(`assessmentQuestions:${key}.choices.${v}`), v)).join(',')) ||
      ''
    )
  }

  return getTranslatedText(t(`assessmentQuestions:${key}.choices.${value}`), value)
}

export const emptyGroupDetails = (equipmentByGroups: EquipmentGroupAccordionProps[]): EquipmentGroupAccordionProps[] =>
  equipmentByGroups.filter(currentEquipmentDetails => currentEquipmentDetails?.equipmentDetails?.length === 0)

export const getGroupName = (id: EquipmentGroupStId, groupOptions: GroupOption[]) =>
  groupOptions.find(option => option.key === id)?.value || ''

export const getSearchResultsForGrouping = (
  equipmentByGroups: EquipmentGroupAccordionProps[],
  equipmentByLocations: EquipmentGroupAccordionProps[],
  searchTerm: string
): {
  filteredEquipmentByGroups: EquipmentGroupAccordionProps[]
  filteredEquipmentByLocations: EquipmentGroupAccordionProps[]
} => {
  if (!searchTerm || searchTerm.trim() === '') {
    return { filteredEquipmentByGroups: equipmentByGroups, filteredEquipmentByLocations: equipmentByLocations }
  }

  const lowercasedSearchTerm = searchTerm.toLowerCase()

  const filteredEquipmentByGroups = equipmentByGroups
    .map(group => {
      // Enhanced filtering to include location description, inventory number, and kone equipment number
      const filteredEquipmentDetails = group.equipmentDetails.filter(detail => {
        const { equipment, locationDescription } = detail

        // Check location description if it exists
        if (locationDescription && locationDescription.toLowerCase().includes(lowercasedSearchTerm)) {
          return true
        }

        // Check specific equipment properties: inventoryNumber and koneEquipmentNumber
        if (
          equipment.inventoryNumber &&
          String(equipment.inventoryNumber).toLowerCase().includes(lowercasedSearchTerm)
        ) {
          return true
        }

        if (
          equipment.koneEquipmentNumber &&
          String(equipment.koneEquipmentNumber).toLowerCase().includes(lowercasedSearchTerm)
        ) {
          return true
        }

        // Check if any other equipment properties match the search term (keeping existing functionality)
        return Object.values(equipment).some(
          value => value && String(value).toLowerCase().includes(lowercasedSearchTerm)
        )
      })

      // If there are matching equipment details, include them in the result
      if (filteredEquipmentDetails.length > 0) {
        return {
          ...group,
          equipmentDetails: filteredEquipmentDetails
        }
      }

      return null
    })
    .filter((group): group is EquipmentGroupAccordionProps => group !== null) // Remove null entries

  const filteredEquipmentByLocations = equipmentByLocations
    .map(location => {
      // Check if the location name matches the search term
      if (location.name.toLowerCase().includes(lowercasedSearchTerm)) {
        return location
      }
      // Enhanced filtering to include location description, inventory number, and kone equipment number
      const filteredEquipmentDetails = location.equipmentDetails.filter(detail => {
        const { equipment } = detail

        // Check specific equipment properties: inventoryNumber and koneEquipmentNumber
        if (
          equipment.inventoryNumber &&
          String(equipment.inventoryNumber).toLowerCase().includes(lowercasedSearchTerm)
        ) {
          return true
        }

        if (
          equipment.koneEquipmentNumber &&
          String(equipment.koneEquipmentNumber).toLowerCase().includes(lowercasedSearchTerm)
        ) {
          return true
        }

        // Check if any other equipment properties match the search term (keeping existing functionality)
        return Object.values(equipment).some(
          value => value && String(value).toLowerCase().includes(lowercasedSearchTerm)
        )
      })

      // If there are matching equipment details, include them in the result
      if (filteredEquipmentDetails.length > 0) {
        return {
          ...location,
          equipmentDetails: filteredEquipmentDetails
        }
      }

      return null
    })
    .filter((location): location is EquipmentGroupAccordionProps => location !== null) // Remove null entries

  return { filteredEquipmentByGroups, filteredEquipmentByLocations }
}

const getDeleteGroupOptions = (equipmentByGroups: EquipmentGroupAccordionProps[]) => {
  const sortedGroups = R.sortBy(R.prop('name'), equipmentByGroups || [])
  const firstGroupWithEquipment = sortedGroups.find(group => group.equipmentDetails.length > 0) || null

  const deleteGroupOptions: DeleteGroupOption[] = sortedGroups.map(group => ({
    key: group.id,
    value: group.name,
    disabled: firstGroupWithEquipment ? group.id === firstGroupWithEquipment.id : false,
    helperText:
      firstGroupWithEquipment && group.id === firstGroupWithEquipment.id
        ? t('grouping.refresh.cannotDeleteGroup')
        : undefined
  }))

  return { defaultGroup: firstGroupWithEquipment, deleteGroupOptions }
}

export const getAllGroupingRelatedData = (
  newBuildings: Building[],
  newEquipmentGroups: EquipmentGroups,
  searchQuery: string
) => {
  const newEquipmentByGroups = getEquipmentDataByGroup(newBuildings, newEquipmentGroups)
  const newEquipmentByLocations = getEquipmentDataByLocation(newBuildings)

  const { filteredEquipmentByGroups, filteredEquipmentByLocations } = getSearchResultsForGrouping(
    newEquipmentByGroups,
    newEquipmentByLocations,
    searchQuery
  )

  const isOnlyDefaultGroupPresent = newEquipmentByGroups.length <= 1

  const { defaultGroup, deleteGroupOptions } = getDeleteGroupOptions(newEquipmentByGroups)

  return {
    groupOptions: getGroupOptions(newEquipmentGroups),
    selectedGroupForEquipment: getSelectedGroupForEquipment(newBuildings),
    selectedGroupForBuilding: getGroupDetailsForLocation(newBuildings),
    rawEquipementByGroups: newEquipmentByGroups,
    rawEquipmentByLocations: newEquipmentByLocations,
    equipmentByGroups: filteredEquipmentByGroups,
    equipmentByLocations: filteredEquipmentByLocations,
    isOnlyDefaultGroupPresent,
    deleteGroupOptions,
    defaultGroup
  }
}

export const isNewGroupNameNotUnique = (newGroupName: string, equipmentGroups: EquipmentGroups): boolean => {
  if (!newGroupName || (newGroupName && newGroupName.trim() === '') || !equipmentGroups) {
    return false
  }

  const groupNames = Object.values(equipmentGroups).map(group => group.name.toLowerCase())
  return groupNames.includes(newGroupName.trim().toLowerCase())
}

export const getUpdatedBuildingsAfterGroupDeletion = (
  buildings: Building[],
  groupIdsToDelete: EquipmentGroupStId[],
  defaultGroup: EquipmentGroupAccordionProps | null
): Building[] => {
  if (!defaultGroup) {
    return buildings
  }

  const { id: defaultGroupId } = defaultGroup

  return R.map((building: Building) => {
    const {
      details: { equipment }
    } = building

    const updatedEquipment = R.map(currentEquipment => {
      if (groupIdsToDelete.includes(currentEquipment.groupId)) {
        return {
          ...currentEquipment,
          groupId: defaultGroupId
        }
      }
      return currentEquipment
    }, equipment)

    return {
      ...building,
      details: {
        ...building.details,
        equipment: updatedEquipment
      }
    }
  }, buildings)
}

export const getEquipmentCountForAccordion = (equipmentDetails: GroupEquipmentDetails[]) => {
  const equipmentCountBasedOnEquipmentCategory = countByEquipmentCategory(R.map(eq => eq.equipment, equipmentDetails))

  const resultBuildingEquipmentArr: BuildingEquipment[] = []
  if (!equipmentCountBasedOnEquipmentCategory) {
    return resultBuildingEquipmentArr
  }

  const sortedEquipmentArray = Object.keys(equipmentCountBasedOnEquipmentCategory)?.sort(
    sortEquipmentCategoriesComparator
  )

  sortedEquipmentArray.forEach(key => {
    resultBuildingEquipmentArr.push({
      category: key as EquipmentCategory,
      element: Icons[key as keyof typeof Icons],
      count: equipmentCountBasedOnEquipmentCategory[key],
      elementDescription: getEquipmentText(key as EquipmentCategory, equipmentCountBasedOnEquipmentCategory[key])
    })
  })

  return resultBuildingEquipmentArr
}

export const isGroupingInprogress = (offer: Offer): boolean => {
  // Special case: Contract renewal (escalation flow) bypasses grouping
  // The escalation workflow goes directly to offers tab, not grouping
  if (offer.isEscalationFlow?.active) {
    return false
  }

  const { buildings, equipmentGroups, prices } = offer

  const storedGroupData = getDataFromSessionStorage<{ buildings: Building[]; equipmentGroups: EquipmentGroups }>(
    'groupingDetails'
  )

  if (
    !buildings ||
    buildings.length === 0 ||
    !equipmentGroups ||
    R.isEmpty(equipmentGroups) ||
    !R.isNil(storedGroupData) ||
    (prices && prices.length === 0)
  ) {
    return true
  }

  // Check if any group has no scopes assigned
  return hasAnyScopeMissing(equipmentGroups)
}
